<template>
  <v-layout row wrap :class="$vuetify.dark ? 'card-dark ' : 'card-light '">
    <v-flex xs12>
      <v-card :key="execution.hexId + 'card-detail'" :loading="detailLoader">
        <v-card-title>
          <span class="body-1 beez-bold">
            Bot execution details
          </span>
        </v-card-title>
        <v-card-text v-if="stack && execution">
          <v-layout row wrap>
            <v-flex xs12 v-if="stack.variables && stack.variables.botMessage">
              <v-textarea
                label="Bot Message"
                readonly
                :value="stack.variables.botMessage.value"
                :outline="$vuetify.dark"
                color="secondary"
                :key="execution.hexId + 'text3'"
              />
            </v-flex>
            <v-flex v-else>
              <v-alert
                :value="true"
                outline
                type="info"
                style="border-radius: 5px"
                class="mb-3"
                >Bot message not found
              </v-alert>
            </v-flex>
            <v-flex xs12 v-if="execution.message">
              <v-textarea
                label="Execution Log"
                readonly
                :value="execution.message"
                :outline="$vuetify.dark"
                color="secondary"
                :key="execution.hexId + 'text4'"
              />
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs12 v-if="can('EXECUTION_USER_WORKFLOW_INPUT')">
              <v-card>
                <v-toolbar dense flat @click="showInput = !showInput">
                  <v-toolbar-title
                    class="body-2 beez-bold "
                    v-text="'User Inputs'"
                  />
                  <v-spacer />
                  <v-btn icon small class="mb-2">
                    <v-icon
                      >{{
                        showInput ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
                      }}
                    </v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text v-if="showInput">
                  <v-flex xs12>
                    <displayExecutionSchema :input="execution.schemaInput" />
                  </v-flex>
                </v-card-text>
              </v-card>
            </v-flex>
            <v-flex
              xs12
              class="pt-3"
              v-if="can('EXECUTION_PROPOSED_BOT_INPUT')"
            >
              <v-card>
                <v-toolbar
                  dense
                  flat
                  @click="showProposedInput = !showProposedInput"
                >
                  <v-toolbar-title
                    class="body-2 beez-bold "
                    v-text="'Bot Inputs'"
                  />
                  <v-spacer></v-spacer>
                  <v-btn icon small class="mb-2">
                    <v-icon
                      >{{
                        showProposedInput
                          ? 'keyboard_arrow_up'
                          : 'keyboard_arrow_down'
                      }}
                    </v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text v-if="showProposedInput">
                  <v-flex xs12>
                    <displayExecutionSchema
                      :input="execution.schemaProposedInput"
                    />
                  </v-flex>
                </v-card-text>
              </v-card>
            </v-flex>
            <v-flex xs12 class="pt-3" v-if="can('EXECUTION_ACTIVITY_DATA')">
              <v-card>
                <v-toolbar dense flat @click="showOutput = !showOutput">
                  <v-toolbar-title
                    class="body-2 beez-bold "
                    v-text="'Execution State'"
                  />
                  <v-spacer />
                  <v-btn icon small class="mb-2">
                    <v-icon
                      >{{
                        showOutput ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
                      }}
                    </v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text v-if="showOutput">
                  <v-flex xs12>
                    <displayExecutionSchema :input="execution.schemaOutput" />
                  </v-flex>
                </v-card-text>
              </v-card>
            </v-flex>
            <v-flex
              xs12
              class="py-3"
              style="width:800px"
              v-if="can('EXECUTION_ADMINISTRATIVE_TASK')"
            >
              <v-card>
                <v-toolbar dense flat @click="showMisc = !showMisc">
                  <v-toolbar-title
                    class="body-2 beez-bold "
                    v-text="'Administrative Tasks'"
                  />
                  <v-spacer />
                  <v-btn icon small class="mb-2">
                    <v-icon
                      >{{
                        showMisc ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
                      }}
                    </v-icon>
                  </v-btn>
                </v-toolbar>
                <v-card-text v-if="showMisc">
                  <v-layout row wrap>
                    <v-card-text>
                      <v-flex fulid>
                        <v-layout row>
                          <v-flex grow>
                            <v-text-field
                              class="pl-1"
                              label="Stack ID"
                              color="secondary"
                              readonly
                              placeholder
                              :value="execution.stackId"
                              :outline="$vuetify.dark"
                              :key="execution + 'text2'"
                            />
                          </v-flex>
                        </v-layout>
                        <v-layout row class="pb-2">
                          <v-flex grow>
                            <v-btn
                              :class="!$vuetify.dark ? 'secondary' : ''"
                              :color="$vuetify.dark ? 'secondary' : ''"
                              :outline="$vuetify.dark"
                              flat
                              v-if="can('EXECUTION_JSON')"
                              @click.stop="downloadState()"
                            >
                              <v-icon>cloud_download</v-icon>
                              <span class="ml-2 text-capitalize"
                                >Download Execution State (as JSON)</span
                              >
                            </v-btn>
                          </v-flex>
                          <v-flex grow>
                            <execution-bot-admin-tasks
                              :execution="execution"
                              @override="overrideExecution"
                              :submit-loader="submitLoader"
                            />
                          </v-flex>
                          <v-flex grow>
                            <execute-bot-based-on-history
                              :execution="execution"
                              :submit-loader="submitLoader"
                              @trigger="triggerNewBotExecution"
                            />
                          </v-flex>
                        </v-layout>
                      </v-flex>
                    </v-card-text>
                  </v-layout>
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-text v-else>
          <v-alert :value="true" type="info" style="border-radius: 5px"
            >Execution not complete or details not found
          </v-alert>
        </v-card-text>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters } from 'vuex'
import ExecutionBotAdminTasks from './ExecutionBotAdminTasks.vue'
import ExecuteBotBasedOnHistory from './ExecuteBotBasedOnHistory'
import moment from 'moment'
import { saveAs } from 'file-saver/FileSaver'
import _ from 'lodash'
import DisplayExecutionSchema from '@/components/execution/DisplayExecutionSchema.vue'
import axios from 'axios'

export default {
  components: {
    DisplayExecutionSchema,
    ExecutionBotAdminTasks,
    ExecuteBotBasedOnHistory
  },
  name: 'execution-detail',
  data: function() {
    return {
      detailLoader: false,
      showOutput: false,
      showInput: false,
      showProposedInput: false,
      submitLoader: false,
      showMisc: false,
      stackTab: 1,
      execution: {},
      stack: {}
    }
  },
  props: {
    executionInput: Object
  },
  async mounted() {
    this.detailLoader = true
    await this.getData()
    this.detailLoader = false
  },
  methods: {
    getFormType(variable) {
      //  STRING, NUMBER, BOOLEAN, OBJECT, DATE, FILE, ARRAY - possible input options
      if (_.isArray(variable.value)) {
        return 'array'
      } else if (_.isString(variable.value)) {
        switch (variable.type) {
          case 'STRING':
            return 'text'
          case 'NUMBER':
            return 'text'
          case 'BOOLEAN':
            return 'checkbox'
          case 'DATE':
            return 'date'
          case 'FILE':
            return 'file'
          default:
            return 'text'
        }
      } else if (_.isDate(variable.value)) {
        return 'date'
      } else if (_.isNumber(variable.value)) {
        return 'text'
      } else if (_.isBoolean(variable.value)) {
        return 'checkbox'
      } else if (_.isUndefined(variable.value)) {
        return 'text'
      } else if (_.isNull(variable.value)) {
        return 'text'
      } else if (_.isObject(variable.value)) {
        return 'object'
      }
    },

    processStackResponse(obj) {
      if (obj.activityType) {
        obj.activityTypeLocal =
          obj.activityType.charAt(0).toUpperCase() +
          obj.activityType.substr(1).toLowerCase()
      }
      obj.createdAtLocal = moment(obj.createdAt)
        .utc()
        .local()
        .format('DD MMM YYYY hh:mm:ss a')
      if (obj.completedAt !== null) {
        obj.completedAtLocal = moment(obj.completedAt)
          .utc()
          .local()
          .format('DD MMM YYYY hh:mm:ss a')
      } else {
        obj.completedAtLocal = null
      }
      return obj
    },

    async getData() {
      if (
        this.executionInput &&
        this.executionInput.executionType === 'BOT' &&
        this.executionInput.stackId
      ) {
        let tempExecution = {}
        let responseExecution = await axios.get(
          `/api/v1/executions/${this.executionInput.hexId}`
        )
        if (responseExecution.data) {
          tempExecution = responseExecution.data
        }

        let responseStack = await axios.get(
          `/api/v1/stacks/${this.executionInput.stackId}`
        )
        if (responseStack.data) {
          this.stack = this.processStackResponse(responseStack.data)
        }

        this.execution = this.fillExecutionObjectForView(
          tempExecution,
          this.stack
        )
      }
    },

    fillExecutionObjectForView(execution, stack) {
      execution.schemaInput = []
      execution.schemaProposedInput = []
      execution.schemaOutput = []

      if (
        execution &&
        execution.botExchangeModel &&
        execution.botExchangeModel.botInputs
      ) {
        for (let [key, value] of Object.entries(
          execution.botExchangeModel.botInputs
        )) {
          if (value) {
            execution.schemaInput.push({
              key: key,
              type: this.getFormType(value),
              options: {
                label: key,
                value: value.value,
                cssClass: 'mx-1 my-1'
              }
            })
          }
        }
      }
      if (
        execution &&
        execution.botExchangeModel &&
        execution.botExchangeModel.proposedBotInputs
      ) {
        for (let [key, value] of Object.entries(
          execution.botExchangeModel.proposedBotInputs
        )) {
          if (value) {
            execution.schemaProposedInput.push({
              key: key,
              type: this.getFormType(value),
              options: {
                label: key,
                value: value.value,
                cssClass: 'mx-1 my-1'
              }
            })
          }
        }
      }
      if (execution && stack) {
        if (stack && stack.variables) {
          for (let [key, value] of Object.entries(stack.variables)) {
            if (value) {
              execution.schemaOutput.push({
                key: key,
                type: this.getFormType(value),
                options: {
                  label: key,
                  value: value.value,
                  cssClass: 'mx-1 my-1'
                }
              })
            }
          }
        }
      }
      return execution
    },
    async overrideExecution(overrideModel) {
      this.submitLoader = true
      try {
        await axios.post(
          `/api/v1/executions/${this.executionInput.hexId}/status/override`,
          overrideModel
        )
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Error in overriding the run status for selected execution',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.submitLoader = false
      this.$store.dispatch('fetchSingleExecution', this.executionId)
    },

    triggerNewBotExecution(executionModel) {
      this.$store
        .dispatch('queueBot', {
          body: executionModel,
          hexId: this.execution.botId
        })
        .then(() => {
          this.$store.dispatch('directNotification', {
            type: 'success',
            message: 'Bot execution request raised Successfully',
            description: 'Bot execution request has been raised Successfully',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.$emit('submit')
        })
        .catch(e => {
          this.$store.dispatch('directNotification', {
            type: 'error',
            message: 'Bot execution request has Failed',
            description:
              'The Bot execution request has failed - ' + e.response
                ? e.response.data.message
                : 'Error unknown! Kindly retry',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
        })
    },
    getSchemaFromStack(obj) {
      let schema = []
      if (obj) {
        for (let [key, value] of Object.entries(obj)) {
          if (value) {
            schema.push({
              key: key,
              type: this.getFormType(value),
              options: {
                label: key,
                value: value.value,
                cssClass: 'mx-1 my-1'
              }
            })
          }
        }
      }
      return schema
    },
    downloadState: function() {
      let fileName = 'state-' + this.execution.hexId + '.json'

      let fileToSave = new Blob([JSON.stringify(this.stack)], {
        type: 'application/json',
        name: fileName
      })

      // Save the file
      saveAs(fileToSave, fileName)
    }
  },
  computed: {
    ...mapGetters({
      can: 'can'
    })
  }
}
</script>
